import { FormControl, Radio } from '@mui/material';
import React, { FC, useEffect, useId, useState } from 'react';

import {
  DateOffsetInput,
  Reminder,
  TimeUnit,
} from '../../../../generated/graphql';
import translate from '../../../../i18n/translate';
import { ReminderItem } from '../../../../models/reminder';
import { getOffsetEntries } from '../../../../utils/offset';
import RemindersPicker from '../../RemindersPicker';

import OffsetCustom from '../OffsetCustom';

import * as Styled from './styled';

type Props = {
  dataOffsetCurrent: DateOffsetInput | null;
  dataOffsetSaved: DateOffsetInput | null;
  dataRemindersSaved: Reminder[];
  requestOffsetSave: (offset: DateOffsetInput | null) => void;
  requestRemindersSave: (reminder: ReminderItem[]) => void;
};

/**
 * Offset component
 *
 * @param props                      Props passed to the component
 * @param props.dataOffsetSaved      Saved offset
 * @param props.dataRemindersSaved   Saved reminders
 * @param props.requestOffsetSave    Request that the offset is saved
 * @param props.requestRemindersSave Request that the reminders are saved
 * @returns                          The component itself
 */
const Offset: FC<Props> = ({
  dataOffsetSaved,
  dataRemindersSaved,
  requestOffsetSave,
  requestRemindersSave,
}) => {
  const [isCustom, setIsCustom] = useState(false);
  const [customValue, setCustomValue] = useState<DateOffsetInput | null>(null);
  const formDescriptionId = useId();
  const entries = getOffsetEntries();

  /**
   * Preselect custom offset if currently saved offset
   * is not defined in offset entries.
   */
  useEffect(() => {
    if (
      dataOffsetSaved &&
      (dataOffsetSaved.unit !== TimeUnit.HOURS ||
        entries.some(entry => entry.value === dataOffsetSaved?.amount) ===
          false)
    ) {
      // setIsCustom(true);
      setCustomValue(dataOffsetSaved);
    }
    // Only run this effect on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle custom value change
   */
  useEffect(() => {
    if (!isCustom || customValue === null) {
      return;
    }

    if (customValue.amount === 0) {
      requestOffsetSave(null);
    }

    requestOffsetSave(customValue);
  }, [customValue, isCustom, requestOffsetSave]);

  /**
   * The user has selected a new offset
   *
   * @param _event The event that took place
   * @param value  The value of the selected offset
   */
  const handleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (value === 'custom') {
      setIsCustom(true);
      return;
    }

    setIsCustom(false);

    if (value === '0') {
      requestRemindersSave([]);
      requestOffsetSave(null);
      return;
    }

    requestOffsetSave({ amount: parseInt(value, 10), unit: TimeUnit.HOURS });
  };

  /**
   * Handle custom value change
   *
   * @returns Current value of the custom offset
   */
  const getCurrentValue = () => {
    if (isCustom && customValue) {
      return 'custom';
    }

    return dataOffsetSaved?.amount ?? '0';
  };

  /**
   * Handle current local value change
   *
   * @param offset Custom offset
   */
  const requestCustomValueChange = (offset: DateOffsetInput) => {
    if (offset.amount === 0) {
      setIsCustom(false);
      setCustomValue(null);
      requestOffsetSave(null);
      return;
    }

    setCustomValue(offset);
    setIsCustom(true);
  };

  /**
   * Check if radio button is checked
   *
   * @param value Radio button value
   * @returns     Whether the button is checked
   */
  const getIsChecked = (value: number | 'custom') => {
    if (dataOffsetSaved === null && value === 0) {
      return !isCustom;
    }

    return (
      !isCustom &&
      value === dataOffsetSaved?.amount &&
      dataOffsetSaved.unit === TimeUnit.HOURS
    );
  };

  return (
    <Styled.Section>
      <FormControl>
        <Styled.FormDescription id={formDescriptionId}>
          {translate('DUE_DATE__RELATIVE_INFO')}
        </Styled.FormDescription>
        <Styled.RadioGroup
          aria-describedby={formDescriptionId}
          onChange={handleChange}
          value={getCurrentValue()}
        >
          {entries.map(entry => (
            <Styled.FormControlLabel
              control={
                <Radio
                  checked={getIsChecked(entry.value)}
                  disableRipple={true}
                />
              }
              key={entry.value}
              label={entry.label}
              value={entry.value}
            />
          ))}
          <Styled.FormControlLabel
            control={<Radio checked={isCustom} disableRipple={true} />}
            key="custom"
            label={
              <OffsetCustom
                customOffset={customValue}
                isRequired={isCustom}
                requestChange={requestCustomValueChange}
              />
            }
            value="custom"
          />
        </Styled.RadioGroup>
      </FormControl>

      <RemindersPicker
        isDisabled={dataOffsetSaved === null}
        reminders={dataRemindersSaved}
        requestOnChange={requestRemindersSave}
      />
    </Styled.Section>
  );
};

export default Offset;
